import React from 'react';
import { Mail, Instagram, ArrowUpRight } from 'lucide-react';
import { SiteHeader } from './SiteHeader';
import { SiteFooter } from './SiteFooter';
import { usePageMeta } from '@/hooks/usePageMeta';

export const ContactPage = () => {
  usePageMeta({
    title: 'Contact — Graves Collective',
    description:
      'Get in touch with the studio of Joseph Graves Jr. — commissions, original inquiries, press, and wholesale.',
  });

  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="page-contact">
      <SiteHeader />

      <section className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 pt-16 lg:pt-24 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-6">
              Contact
            </p>
            <h1 className="font-display text-5xl lg:text-6xl xl:text-7xl leading-[0.95] tracking-tight text-ink">
              Say hello to{' '}
              <span className="italic text-terracotta">the studio.</span>
            </h1>
            <p className="mt-10 text-lg lg:text-xl text-soft max-w-xl leading-relaxed">
              Commissions, original painting inquiries, press, wholesale, or
              just to say you liked something — the studio reads every message.
            </p>
          </div>

          <div className="lg:col-span-5 lg:pt-10 space-y-10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-3">
                Email
              </p>
              <a
                href="mailto:hello@josephgravesjr.com"
                className="group inline-flex items-center gap-3 text-2xl font-display text-ink hover:text-terracotta transition-colors"
                data-testid="contact-email"
              >
                <Mail className="w-5 h-5" />
                hello@josephgravesjr.com
              </a>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-3">
                Studio
              </p>
              <address
                className="text-lg text-ink leading-relaxed not-italic"
                data-testid="contact-studio-address"
              >
                900 Santa Fe Drive, Studio D
                <br />
                Denver, CO 80204
              </address>
              <p className="mt-3 text-sm text-soft leading-relaxed">
                Open to the public during{' '}
                <a
                  href="https://denversantafe.org/first-friday-art-walk"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-ink hover:text-terracotta transition-colors underline decoration-ink/20 hover:decoration-terracotta"
                  data-testid="contact-first-friday-link"
                >
                  First Friday Art Walks
                </a>
                . Visits by appointment otherwise — drop a note via email.
              </p>
              <a
                href="https://maps.google.com/?q=900+Santa+Fe+Drive+Studio+D+Denver+CO+80204"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-ink hover:text-terracotta transition-colors group"
                data-testid="contact-directions-link"
              >
                Get directions
                <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
            </div>

            <div>
              <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-3">
                Elsewhere
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href="https://josephgravesjr.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-base text-ink hover:text-terracotta transition-colors"
                  data-testid="contact-artist-site"
                >
                  Joseph Graves Jr. — Artist Site
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </a>
                <a
                  href="https://instagram.com/josephgravesjr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-3 text-base text-ink hover:text-terracotta transition-colors"
                  data-testid="contact-instagram"
                >
                  <Instagram className="w-4 h-4" />
                  Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Studio map band */}
      <section
        className="border-t border-ink/10 bg-cream-deep/30"
        data-testid="contact-map-section"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-4">
              <p className="text-[11px] uppercase tracking-[0.24em] text-soft mb-6">
                Find the Studio
              </p>
              <h2 className="font-display text-3xl lg:text-4xl text-ink tracking-tight leading-tight">
                In the heart of Denver&apos;s{' '}
                <span className="italic text-terracotta">Art District.</span>
              </h2>
              <p className="mt-6 text-soft leading-relaxed">
                900 Santa Fe Drive is the spine of Denver&apos;s Art District —
                a working stretch of galleries, studios, and makers. Studio D
                is on the ground floor. Look for the wordmark on the door.
              </p>
            </div>
            <div className="lg:col-span-8">
              <div className="relative overflow-hidden aspect-[16/10] bg-ink/[0.04] border border-ink/10">
                <iframe
                  title="Graves Collective Studio — 900 Santa Fe Drive, Studio D, Denver, CO"
                  src="https://www.google.com/maps?q=900+Santa+Fe+Drive,+Denver,+CO+80204&output=embed"
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="studio-map-embed"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default ContactPage;
